import { clientGET } from '@/api/client'
import { Game, GameState, Schedule } from '@/types'
import { useCallback, useEffect, useState } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'

export type ReloadStatus = 'loading' | 'error' | 'idle' | 'success'

// Define the structure of each game's livescore data
interface GameLiveScore {
  id: string
  state: string
  homeGoals: number
  awayGoals: number
  updatedAt: number
}

// Define the structure of the API response
interface LiveScoreResponse {
  data: GameLiveScore[]
}

interface UseScheduleSearchProps {
  schedule: Schedule
  refreshInterval?: number // in seconds
  loadingThreshold?: number // in milliseconds
  successAnimationDelay?: number // in seconds
}

export function useScheduleSearch({
  schedule,
  refreshInterval,
  loadingThreshold = 500,
  successAnimationDelay = 1
}: UseScheduleSearchProps): {
  data: Schedule
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
  reset: (newData: Schedule) => void
  reloadStatus: ReloadStatus
} {
  const [data, setData] = useState<Schedule>(schedule)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [reloadStatus, setReloadStatus] = useState<ReloadStatus>('idle')

  useEffect(() => {
    if (reloadStatus === 'success') {
      setTimeout(() => {
        setReloadStatus('loading')
      }, successAnimationDelay * 1000)
    }
  }, [reloadStatus])

  const fetchSchedule = useCallback(async () => {
    let loadingTimer: NodeJS.Timeout | null = null
    try {
      loadingTimer = setTimeout(() => {
        setLoading(false)
      }, loadingThreshold)

      const gameIds = schedule
        .filter((game) => gameCouldBeLive(game))
        .map((game) => game.id)
        .join(',')
      if (gameIds.length > 0) {
        const url = `/sportdata/1/games/livescores`
        const response = await clientGET({ url, params: { gameIds: gameIds } })

        const result = (await response) as LiveScoreResponse

        // Map the live scores to the original schedule
        const updatedSchedule = mapLiveScoresToSchedule(schedule, result.data)
        setData(updatedSchedule)
        setError(null)
        setReloadStatus('success')

        // If we have a result before the timeout ends, clear it immediately and set loading to false
        if (loadingTimer) {
          clearTimeout(loadingTimer)
          loadingTimer = null
          setLoading(false)
        }
      } else {
        // No games to fetch, clear timer and set loading to false
        setReloadStatus('idle')
        if (loadingTimer) {
          clearTimeout(loadingTimer)
          loadingTimer = null
          setLoading(false)
        }
      }
    } catch (err) {
      setError(err as Error)
      setLoading(false)
      setReloadStatus('error')

      // Clear the timeout if it exists
      if (loadingTimer) {
        clearTimeout(loadingTimer)
        loadingTimer = null
      }
    }
  }, [schedule, setData, setLoading, setError, loadingThreshold])

  const resetData = useCallback(
    (newSchedule: Schedule) => {
      setData(newSchedule)
      setReloadStatus('loading')
    },
    [setData]
  )

  // this ensures that the hook works when it is first called with an empty input because the input is still loading
  // once the input data is loaded or every time it changes (round change) it is reset and an inital fetch is triggered
  // deep is comparison needed to prevent endless loop when this is called with an input like schedule.slice(0,4)
  useDeepCompareEffect(() => {
    setLoading(true)
    setReloadStatus('loading')
    resetData(schedule)
    fetchSchedule()
  }, [schedule])

  // Set up periodic refresh if refreshInterval is provided
  useEffect(() => {
    if (!refreshInterval) return

    const intervalId = setInterval(() => {
      fetchSchedule()
    }, refreshInterval * 1000)

    return () => clearInterval(intervalId)
  }, [refreshInterval, fetchSchedule])

  return {
    data,
    loading,
    error,
    refetch: fetchSchedule,
    reset: resetData,
    reloadStatus
  }
}

// Function to map live scores to the original schedule
function mapLiveScoresToSchedule(
  originalSchedule: Schedule,
  liveScores: GameLiveScore[]
): Schedule {
  // Create a map of game IDs to live scores for quick lookup
  const liveScoreMap = new Map<string, GameLiveScore>()
  liveScores.forEach((score) => {
    liveScoreMap.set(score.id, score)
  })

  // Update each game in the schedule with its corresponding live score data
  return originalSchedule.map((game) => {
    const liveScore = liveScoreMap.get(game.id)

    if (!liveScore) {
      return game
    }

    // Create a new game object with updated data
    return {
      ...game,
      homeGoals: liveScore.homeGoals,
      awayGoals: liveScore.awayGoals,
      state: liveScore.state as GameState,
      updatedAt: liveScore.updatedAt
    } as Game
  })
}

function gameCouldBeLive(game: Game) {
  const today = new Date()
  const upperEnd = new Date()
  const lowerEnd = new Date()
  upperEnd.setHours(today.getHours() + 10)
  lowerEnd.setHours(today.getHours() - 10)
  const startDate = new Date(game.startsAt)
  return startDate >= lowerEnd && startDate <= upperEnd
}
